// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import "firebase/auth";
import "firebase/database";
import "firebase/firestore";
import "firebase/storage";

import {
  NewTestLogsMetadata,
  Registration,
  TestRun,
  UserInterpretation,
} from "store/testrun";
import {
  UNSET_PROFILE_ID,
  UNSET_USER_ID,
} from "components/TestRun/TestRunConstants";

import { CheckRegistrationResponse } from "SharedTypes";
import { FEDEX_PRODUCTION_TOKEN } from "components/Shipping/FedEx/FedExConstants";
import { FedExConfirmation } from "components/Shipping/FedEx/FedExTypes";
import { SYMPTOMS } from "components/SymptomsChecker/SymptomsConstants";
import firebase from "firebase/app";
import { getAppConfig } from "utils/AppConfig";
import { getEnvironmentAsync } from "utils/useEnvironment";
import { uuid } from "uuidv4";

interface Firebase {
  auth: firebase.auth.Auth;
  firestore: firebase.firestore.Firestore;
  storage: firebase.storage.Storage;
  config: any;
  remoteConfig: firebase.remoteConfig.RemoteConfig;
}

class Firebase {
  constructor(firebaseConfig: any) {
    this.config = firebaseConfig;
    firebase.initializeApp(firebaseConfig);

    this.auth = firebase.auth();
    this.firestore = firebase.firestore();
    this.storage = firebase.storage();

    const remoteConfig = firebase.remoteConfig();
    remoteConfig.settings = {
      minimumFetchIntervalMillis: 3600000, // every hour
      fetchTimeoutMillis: 60000,
    };
    // TODO: generalize this concept.
    remoteConfig.defaultConfig = {
      showFedExAPIWarningInProd: getAppConfig().showFedExAPIWarningInProd,
      enableFedExPickupInProd: getAppConfig().enableFedExPickupInProd,
    };
    this.remoteConfig = remoteConfig;
    // Note: this async fetch will fail silently if there's a problem and use
    // the cached store in the browser or the default settings.
    this.remoteConfig.fetchAndActivate();
  }

  getProjectId(): string {
    return this.config?.projectId || "";
  }

  isStagingBackend(): boolean {
    return this.getProjectId() === "openrdt-dashboard-staging";
  }

  isProductionBackend(): boolean {
    return this.getProjectId() === "openrdt-dashboard-production";
  }

  /**
   * Authentication Calls
   **/

  createUserWithEmailAndPassword = (
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> =>
    this.auth.createUserWithEmailAndPassword(email, password);

  signInWithEmailAndPassword = (
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> =>
    this.auth.signInWithEmailAndPassword(email, password);

  signOut = (): Promise<void> => this.auth.signOut();

  passwordReset = (email: string): Promise<void> =>
    this.auth.sendPasswordResetEmail(email);

  /**
   * Profile Accessors
   */

  createUser = async (params: {
    orgId: string;
    userUID: string;
    email: string;
  }) => {
    await this.createOrg(params.orgId);
    await this.getUserDocumentRef(params).set({
      uid: params.userUID,
    });
    await this.getUserPrivateInfoRef(params).set({
      email: params.email,
    });
  };

  getUserOrgId = async (
    userUID: string,
    email: string | null
  ): Promise<string | null> => {
    const users = await this.firestore
      .collectionGroup("users")
      .where("uid", "==", userUID)
      .get();
    if (users.size > 1) {
      throw new Error(`More than one user was found with uid ${userUID}`);
    }
    if (users.size === 1) {
      const userDoc = users.docs[0];
      const orgRef = userDoc.ref.parent.parent;
      if (!orgRef) {
        throw new Error(`No org doc found for user with uid ${userUID}`);
      }
      const orgDoc = (await orgRef.get()).data();
      const allowedUserList: string[] = orgDoc?.allowedUserList;
      if (allowedUserList && (!email || !allowedUserList.includes(email))) {
        console.error(`User with email ${email} not authorized for this org`);
        return null;
      }
      return orgRef.id as string;
    }
    return null;
  };

  getUserDocumentRef = ({
    orgId,
    userUID,
  }: {
    orgId: string;
    userUID: string;
  }) =>
    this.getOrgRef(orgId)
      .collection("users")
      .doc(userUID);

  getUserPrivateInfoRef = (params: { orgId: string; userUID: string }) => {
    return this.getUserDocumentRef(params)
      .collection("private")
      .doc("info");
  };

  doesUserExist = async (params: { orgId: string; userUID: string }) => {
    const user = await this.getUserDocumentRef(params).get();
    return user.exists;
  };

  addProfileToUser = async (params: {
    profile: any;
    orgId: string;
    userUID: string;
  }) => {
    await this.createOrg(params.orgId);
    const profileUID = uuid();
    const profileDocumentRef = this.getUserProfileDocumentRef({
      profileUID,
      ...params,
    });
    await profileDocumentRef.set({
      ...params.profile,
      dateCreated: new Date().toISOString(),
      uid: profileUID,
    });
    return profileDocumentRef;
  };

  fetchUserProfile = async (params: {
    orgId: string;
    userUID: string;
    profileUID: string;
  }) => {
    const profileDoc = await this.getUserProfileDocumentRef(params).get();
    return profileDoc.data();
  };

  getUserProfileCollectionRef = (params: { orgId: string; userUID: string }) =>
    this.getUserDocumentRef(params).collection("profiles");

  getUserProfileDocumentRef = (params: {
    orgId: string;
    userUID: string;
    profileUID: string;
  }) => {
    return this.getUserProfileCollectionRef(params).doc(params.profileUID);
  };

  getDefaultProfileUID = async (params: {
    orgId: string;
    userUID: string;
  }): Promise<string> => {
    const userDocumentRef = this.getUserDocumentRef(params);
    const user = (await userDocumentRef.get()).data();
    if (!user?.defaultProfileUID) {
      const profileDoc = await this.addProfileToUser({
        profile: {},
        ...params,
      });
      await userDocumentRef.set(
        {
          defaultProfileUID: profileDoc.id,
        },
        { merge: true }
      );
      return profileDoc.id;
    }
    return user.defaultProfileUID;
  };

  addCompletedTestToProfile = async (params: {
    orgId: string;
    profileUID: string;
    testRunUID: string;
    userUID: string;
  }) => {
    params.profileUID !== UNSET_PROFILE_ID &&
      this.getUserProfileCollectionRef(params)
        .doc(params.profileUID)
        .update({
          lastCompletedTest: params.testRunUID,
        });
  };

  /**
   * TestRuns Accessors
   */

  getTestRunCollectionRef = (params: { orgId: string }) => {
    return this.getOrgRef(params.orgId).collection("testruns");
  };

  getTestRunByIDRef = (params: { orgId: string; testRunUID: string }) =>
    this.getTestRunCollectionRef(params).doc(params.testRunUID);

  fetchTestRunByID = async (params: {
    orgId: string;
    testRunUID: string;
  }): Promise<TestRun | null> => {
    const testRun = this.getTestRunByIDRef(params);
    const testRunSnapshot = await testRun.get();
    const testRunDetails = testRunSnapshot.data() as TestRun;
    return testRunDetails || null;
  };

  addTestRunToUser = async (params: {
    orgId: string;
    profileUID?: string;
    userUID: string;
    startStep: string;
  }) => {
    let { profileUID } = params;
    const { userUID } = params;
    if (!profileUID) {
      if (userUID === UNSET_USER_ID) {
        const profileDoc = await this.addProfileToUser({
          profile: {},
          ...params,
        });
        profileUID = profileDoc.id;
      } else {
        profileUID = await this.getDefaultProfileUID(params);
      }
    }
    const newTestRun = await this.getTestRunCollectionRef(params).add({
      lastStep: params.startStep,
      timestamp: Date.now(),
      profileUID: profileUID,
      orgId: params.orgId,
      userUID: params.userUID,
      steps: {},
    });
    const testRunUID = newTestRun.id;
    await newTestRun.update({
      uid: testRunUID,
    });
    return testRunUID;
  };

  setTestRunProfile = async (params: {
    orgId: string;
    profileUID: string;
    testRunUID: string;
  }) => {
    return await this.getTestRunByIDRef(params).update({
      profileUID: params.profileUID,
    });
  };

  logNewTestRunMetadata = async (params: NewTestLogsMetadata) => {
    firebaseApp.firestore.collection("newTestRunLogs").add(params);
  };

  updateTestRunStepTimestamp = async (params: {
    step: string;
    testRunUID: string;
    orgId: string;
    userUID: string;
  }) => {
    const testRun = this.getTestRunByIDRef(params);
    const testRunSnapshot = await testRun.get();
    const testRunData = testRunSnapshot.data();
    const steps = testRunData!.steps || {};
    const now = Date.now();

    if (steps[params.step] === undefined) {
      steps[params.step] = {
        firstVisitedTime: now,
      };
    }
    steps[params.step].lastVisitedTime = now;

    await testRun.update({
      lastStep: params.step,
      steps: steps,
      timestamp: now,
    });
  };

  logTestRunNavEvent = async (params: {
    step: string;
    testRunUID: string;
    orgId: string;
    userUID: string;
  }) => {
    const testRun = this.getTestRunByIDRef(params);
    const testRunSnapshot = await testRun.get();
    const testRunData = testRunSnapshot.data()!;
    const navEvents = testRunData.navEvents || [];
    navEvents.push({
      timestamp: new Date().toISOString(),
      screen: params.step,
    });
    await testRun.update({ navEvents });
  };

  setTestRunDemoMode = async (params: {
    testRunUID: string;
    orgId: string;
    userUID: string;
  }) => {
    await this.getTestRunByIDRef(params).set(
      {
        demoMode: true,
      },
      { merge: true }
    );
  };

  updateTestRunSymptomsList = async (params: {
    reportedSymptoms: { [name: string]: boolean };
    testRunUID: string;
    orgId: string;
    userUID: string;
  }) => {
    this.getTestRunByIDRef(params).update({
      reportedSymptoms: SYMPTOMS.reduce((acc, symptom) => {
        if (params.reportedSymptoms[symptom.name]) {
          acc[symptom.name] = params.reportedSymptoms[symptom.name];
        }
        return acc;
      }, {}),
    });
  };

  updateTestRunUserInterpretation = async (params: {
    userInterpretation: UserInterpretation;
    testRunUID: string;
    orgId: string;
    userUID: string;
  }) =>
    this.getTestRunByIDRef(params).set(
      {
        userInterpretation: params.userInterpretation,
      },
      { merge: true }
    );

  updateTestRunRegistration = async (params: {
    testRunUID: string;
    orgId: string;
    userUID: string;
    barcode: string;
    registrationID: string;
    registrationToken: string;
  }) =>
    this.getTestRunByIDRef(params).set(
      {
        registration: {
          barcode: params.barcode,
          registrationID: params.registrationID,
        },
        registrationToken: params.registrationToken,
      },
      { merge: true }
    );

  updateTestRunScheduledPickup = async (
    testRun: { testRunUID: string; orgId: string; userUID: string },
    confirmation: FedExConfirmation
  ) => {
    await this.getTestRunByIDRef(testRun).update({
      scheduledPickup: confirmation,
    });
  };

  /**
   * Photo Accessors
   */

  uploadRDTPhoto = async (params: {
    imageAsFile?: File | null;
    imageAsURI?: string;
    testRunUID: string;
    onError?: (err: Error) => void;
    onFileUploadProgress?: (
      snapshot: firebase.storage.UploadTaskSnapshot
    ) => void;
    onFileUploadComplete: (params: { imgUrl: string }) => void;
  }) => {
    const {
      imageAsFile,
      imageAsURI,
      testRunUID,
      onError,
      onFileUploadComplete,
      onFileUploadProgress,
    } = params;

    if (!imageAsFile && !imageAsURI) {
      throw Error("uploadPhoto needs either a File or a dataURI");
    }

    const filename = uuid();
    const ref = this.storage.ref(`/images/${testRunUID}/${filename}`);
    let uploadTask;
    if (imageAsFile) {
      uploadTask = ref.put(imageAsFile);
    } else {
      uploadTask = ref.putString(imageAsURI!, "data_url");
    }
    //initiates the firebase side uploading
    uploadTask.on(
      "state_changed",
      (snapShot: firebase.storage.UploadTaskSnapshot) => {
        onFileUploadProgress && onFileUploadProgress(snapShot);
      },
      (err: Error) => {
        console.log("Error File during Upload", err);
        onError && onError(err);
      },
      async () => {
        const firebaseUrl = await this.storage
          .ref(`images/${testRunUID}`)
          .child(`${filename}`)
          .getDownloadURL();
        onFileUploadComplete({ imgUrl: firebaseUrl });
      }
    );
  };

  /**
   * Organization Accessors
   */
  getOrgRef = (orgId: string) => {
    return firebaseApp.firestore.collection("orgs").doc(orgId);
  };

  createOrg = async (orgId: string) => {
    const orgQuery = this.getOrgRef(orgId);
    const org = await orgQuery.get();
    if (!org.exists) {
      await orgQuery.set({});
    }
  };

  /**
   * Registration data
   */
  // TODO: USC-1205 move to cloud function
  uploadRegistrations = async (
    registrations: Registration[],
    orgId: string
  ) => {
    const indexedRegistrations = {};
    registrations.forEach(registration => {
      indexedRegistrations[registration.barcode] = registration;
    });

    const org = this.getOrgRef(orgId);
    await org.set(
      {
        registrations: indexedRegistrations,
      },
      { merge: true }
    );
  };

  private scheduleFedExHitsProd = async (
    demoMode: boolean
  ): Promise<boolean> => {
    const env = await getEnvironmentAsync();
    return (
      !demoMode &&
      !env.isDevFrontend &&
      !env.isStagingFrontend &&
      env.isProductionFrontend &&
      env.isProductionBackend &&
      this.remoteConfig.getBoolean("enableFedExPickupInProd")
    );
  };

  checkRegistration = async (
    registrationID: string,
    barcode: string,
    orgId: string,
    testRunUID: string,
    userUID: string
  ): Promise<CheckRegistrationResponse> => {
    const checkRegistrationCallable = firebase
      .functions()
      .httpsCallable("checkRegistration");
    const registrationParams: any = {
      registrationID,
      barcode,
      orgId,
    };
    const testRunDocument = await this.getTestRunByIDRef({
      testRunUID,
      orgId,
    });
    const testRunSnapshot = await testRunDocument.get();
    const testRun = testRunSnapshot.data() as TestRun;
    const shouldFedExHitProd = await this.scheduleFedExHitsProd(
      !!testRun?.demoMode
    );
    if (shouldFedExHitProd) {
      console.log("This will REALLY schedule");
      registrationParams.productionToken = FEDEX_PRODUCTION_TOKEN;
    } else {
      console.log("This WILL NOT schedule");
    }
    const response: CheckRegistrationResponse = (
      await checkRegistrationCallable(registrationParams)
    ).data;
    return response;
  };

  // TODO: USC-1205 move to cloud function
  redeemRegistration = async (barcode: string, orgId: string) => {
    const org = this.getOrgRef(orgId);
    await org.set(
      {
        registrations: {
          [barcode]: {
            activated: true,
          },
        },
      },
      { merge: true }
    );
  };

  // TODO: USC-1205 move to cloud function
  updateEmail = async (email: string, barcode: string, orgId: string) => {
    const org = this.getOrgRef(orgId);
    await org.set(
      {
        registrations: {
          [barcode]: {
            email: email,
          },
        },
      },
      { merge: true }
    );
  };
}

let firebaseApp: Firebase;

const loadConfig = async (): Promise<any> => {
  const response = await fetch("/__/firebase/init.json");
  const config = await response.json();
  return config;
};

export const getFirebaseApp = async (): Promise<Firebase> => {
  if (!firebaseApp) {
    const config = await loadConfig();
    // testing again in case there were two concurrent loads.
    if (!firebaseApp) {
      firebaseApp = new Firebase(config);
    }
  }
  return firebaseApp;
};

// Call this only if you are sure the firebase app is already be properly initialized.
export const getFirebaseAppSync = (): Firebase => {
  if (!firebaseApp) {
    throw new Error(
      `
      getFirebaseAppSync was called before the firebase app was initialized. 
      Switch to an await getFirebaseApp()
      `
    );
  }
  return firebaseApp;
};

export default Firebase;
